import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./lib/auth";
import { SettingsProvider } from "./lib/settings";
import { ChatProvider } from "./lib/chat";
import { I18nProvider } from "./lib/i18n";
import { Toaster } from "sonner";
import AppLayout from "./components/AppLayout";
import PublicLayout from "./components/PublicLayout";
import PublicGate from "./components/PublicGate";
import { canOpen } from "./lib/access";
import ThemeProvider from "./components/ThemeProvider";
import AdminThemeProvider from "./components/AdminThemeProvider";
import LoginPage from "./pages/LoginPage";
import NotFoundPage from "./pages/public/NotFoundPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import DashboardPage from "./pages/DashboardPage";
import InventoryListPage from "./pages/InventoryListPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import VehicleFormPage from "./pages/VehicleFormPage";
import UsersPage from "./pages/UsersPage";
import PermissionsPage from "./pages/PermissionsPage";
import SettingsPage from "./pages/SettingsPage";
import ShowroomFormPage from "./pages/ShowroomFormPage";
import KioskFormPage from "./pages/KioskFormPage";
import CommissionsPage from "./pages/CommissionsPage";
import SocialAccountsPage, { SocialCallbackPage } from "./pages/SocialAccountsPage";
import SocialSuggestionsPage from "./pages/SocialSuggestionsPage";
import SocialSamplesPage from "./pages/SocialSamplesPage";
import PublicFormPage from "./pages/public/PublicFormPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import LiveChatPage from "./pages/LiveChatPage";
import ChatDemoPage from "./pages/ChatDemoPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import SalesPage from "./pages/SalesPage";
import PaymentsPage from "./pages/PaymentsPage";
import DocumentsPage from "./pages/DocumentsPage";
import FormsPage from "./pages/FormsPage";
import ReportsPage from "./pages/ReportsPage";
// Public pages
import HomePage from "./pages/public/HomePage";
import ListingPage from "./pages/public/ListingPage";
import PublicVehicleDetailPage from "./pages/public/VehicleDetailPage";
import SellYourCarPage from "./pages/public/SellYourCarPage";
import ContactPage from "./pages/public/ContactPage";
import AboutPage from "./pages/public/AboutPage";
import FinancePage from "./pages/public/FinancePage";
import PayLinkPage from "./pages/public/PayLinkPage";
import PayResultPage from "./pages/public/PayResultPage";
import OnlinePaymentsPage from "./pages/OnlinePaymentsPage";
import { tr } from "./lib/i18n";
import "./public.css";

function Protected({ children, screen }) {
  const { user, loading, can } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--gray-400)]">{tr("x.loading")}</div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user.must_change_password && loc.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (screen && !canOpen(screen, user, can)) return <Navigate to="/dashboard" replace />;
  const kiosk = sessionStorage.getItem("kiosk_lock");
  if (kiosk && !loc.pathname.startsWith("/kiosk/")) return <Navigate to={`/kiosk/${kiosk}`} replace />;
  return children;
}


function Shell({ children }) { return <AdminThemeProvider><AppLayout>{children}</AppLayout></AdminThemeProvider>; }
function Pub({ children }) { return <PublicGate><ThemeProvider><PublicLayout>{children}</PublicLayout></ThemeProvider></PublicGate>; }

function App() {
  // Global safety net: swallow any stray axios rejection so a page-load
  // race never triggers CRA's red "Uncaught runtime errors" overlay.
  useEffect(() => {
    const onRej = (ev) => {
      const err = ev.reason;
      if (err && (err.isAxiosError || err?.response || err?.config?.url)) {
        ev.preventDefault();
        console.warn("[handled axios rejection]", err?.config?.url, err?.response?.status);
      }
    };
    window.addEventListener("unhandledrejection", onRej);
    return () => window.removeEventListener("unhandledrejection", onRej);
  }, []);

  return (
    <I18nProvider>
      <SettingsProvider>
        <AuthProvider>
          <ChatProvider>
            <BrowserRouter>
              <Toaster richColors position="top-right" containerAriaLabel={tr("x.notifications")} />
              <Routes>
                {/* ---- PUBLIC SHOWROOM SITE ---- */}
                <Route path="/" element={<Pub><HomePage /></Pub>} />
                <Route path="/cars" element={<Pub><ListingPage /></Pub>} />
                <Route path="/cars/:ident" element={<Pub><PublicVehicleDetailPage /></Pub>} />
                <Route path="/sell" element={<Pub><SellYourCarPage /></Pub>} />
                <Route path="/finance" element={<Pub><FinancePage /></Pub>} />
                <Route path="/about" element={<Pub><AboutPage /></Pub>} />
                <Route path="/contact" element={<Pub><ContactPage /></Pub>} />
                <Route path="/pay/result" element={<Pub><PayResultPage /></Pub>} />
                <Route path="/pay/:token" element={<Pub><PayLinkPage /></Pub>} />

                {/* ---- ADMIN LOGIN & PUBLIC-ACCESSIBLE ---- */}
                <Route path="/login" element={<AdminThemeProvider><LoginPage /></AdminThemeProvider>} />
                <Route path="/f/:token" element={<PublicFormPage />} />
                <Route path="/chat-demo" element={<ChatDemoPage />} />
                <Route path="/admin" element={<Navigate to="/dashboard" replace />} />

                {/* ---- ADMIN DASHBOARD ---- */}
                <Route path="/change-password" element={<Protected><AdminThemeProvider><ChangePasswordPage /></AdminThemeProvider></Protected>} />
                <Route path="/dashboard" element={<Protected screen="/dashboard"><Shell><DashboardPage /></Shell></Protected>} />
                <Route path="/inventory" element={<Protected screen="/inventory"><Shell><InventoryListPage /></Shell></Protected>} />
                <Route path="/inventory/new" element={<Protected screen="/inventory/new"><Shell><VehicleFormPage /></Shell></Protected>} />
                <Route path="/inventory/:id" element={<Protected screen="/inventory/:id"><Shell><VehicleDetailPage /></Shell></Protected>} />
                <Route path="/inventory/:id/edit" element={<Protected screen="/inventory/:id/edit"><Shell><VehicleFormPage /></Shell></Protected>} />
                <Route path="/live-chat" element={<Protected screen="/live-chat"><Shell><LiveChatPage /></Shell></Protected>} />
                <Route path="/customers" element={<Protected screen="/customers"><Shell><CustomersPage /></Shell></Protected>} />
                <Route path="/customers/:id" element={<Protected screen="/customers/:id"><Shell><CustomerDetailPage /></Shell></Protected>} />
                <Route path="/sales" element={<Protected screen="/sales"><Shell><SalesPage /></Shell></Protected>} />
                <Route path="/payments" element={<Protected screen="/payments"><Shell><PaymentsPage /></Shell></Protected>} />
                <Route path="/online-payments" element={<Protected screen="/online-payments"><Shell><OnlinePaymentsPage /></Shell></Protected>} />
                <Route path="/documents" element={<Protected screen="/documents"><Shell><DocumentsPage /></Shell></Protected>} />
                <Route path="/forms" element={<Protected screen="/forms"><Shell><FormsPage /></Shell></Protected>} />
                <Route path="/forms/new/:type" element={<Protected screen="/forms"><Shell><ShowroomFormPage /></Shell></Protected>} />
                <Route path="/forms/sf/:id" element={<Protected screen="/forms/sf"><Shell><ShowroomFormPage /></Shell></Protected>} />
                <Route path="/kiosk/:id" element={<Protected><KioskFormPage /></Protected>} />
                <Route path="/commissions" element={<Protected screen="/commissions"><Shell><CommissionsPage /></Shell></Protected>} />
                <Route path="/social/accounts" element={<Protected screen="/social/accounts"><Shell><SocialAccountsPage /></Shell></Protected>} />
                <Route path="/social/suggestions" element={<Protected screen="/social/suggestions"><Shell><SocialSuggestionsPage /></Shell></Protected>} />
                <Route path="/social/samples" element={<Protected screen="/social/samples"><Shell><SocialSamplesPage /></Shell></Protected>} />
                <Route path="/social/accounts/callback" element={<Protected screen="/social/accounts"><Shell><SocialCallbackPage /></Shell></Protected>} />
                <Route path="/reports" element={<Protected screen="/reports"><Shell><ReportsPage /></Shell></Protected>} />
                <Route path="/users" element={<Protected screen="/users"><Shell><UsersPage /></Shell></Protected>} />
                <Route path="/permissions" element={<Protected screen="/permissions"><Shell><PermissionsPage /></Shell></Protected>} />
                <Route path="/activity" element={<Protected screen="/activity"><Shell><ActivityLogPage /></Shell></Protected>} />
                <Route path="/settings" element={<Protected screen="/settings"><Shell><SettingsPage /></Shell></Protected>} />

                <Route path="*" element={<Pub><NotFoundPage /></Pub>} />
              </Routes>
            </BrowserRouter>
          </ChatProvider>
        </AuthProvider>
      </SettingsProvider>
    </I18nProvider>
  );
}

export default App;
